<script lang="ts">
  /**
   * `/pair?code=…` — the browser half of the Android pairing flow (brief §11.7).
   *
   * The app opened this in a Custom Tab because `navigator.credentials` does not exist inside
   * a WebView. Here we are in a real browser with the real credential store, so the passkey
   * works; approving hands the code back to the app, which exchanges it for a device token.
   *
   * The screen is deliberately explicit about what is being approved. A code in a URL that
   * silently grants a 180-day token to whoever holds it is exactly the shape of a phishing
   * page, so this one names the device and asks.
   *
   * **The code can also be typed.** The phone supplies it in the URL because the app opened
   * the tab; the watch cannot — it has no browser to open and no way to hand anything over,
   * so all it can do is show eight characters and wait. Without a field for them the watch
   * flow ended at "and now what", which is where it ended the first time somebody tried it.
   */
  import { ApiError, request } from './api.ts';
  import { describeError, session } from './session.svelte.ts';
  import SignIn from './SignIn.svelte';

  type Props = { code: string; requireInvite: boolean };
  const { code, requireInvite }: Props = $props();

  // Whatever came in the URL, or whatever gets typed. Upper-cased on the way in because the
  // code is shown in capitals on a watch and nobody types it back in lower case on purpose.
  // svelte-ignore state_referenced_locally
  let typed = $state(code);
  const entered = $derived(typed.trim().toUpperCase());

  let busy = $state(false);
  let done = $state(false);
  let error = $state('');
  let deviceName = $state<string | null>(null);

  async function approve() {
    busy = true;
    error = '';
    try {
      const result = await request<{ deviceName: string | null }>('/api/auth/pair/approve', {
        method: 'POST',
        body: JSON.stringify({ code: entered }),
      });
      deviceName = result.deviceName;
      done = true;
    } catch (cause) {
      error = cause instanceof ApiError ? cause.message : describeError(cause);
    } finally {
      busy = false;
    }
  }
</script>

<main>
  <section>
    <h1>App koppeln</h1>

    {#if session.loading}
      <p class="hint">Einen Moment …</p>
    {:else if session.user === null}
      <p class="hint">Melde dich zuerst an, dann kannst du die App freigeben.</p>
      <!-- No install link here: this page is the Custom Tab opened *by* the app, so whoever
           is reading it has already installed it. -->
      <SignIn {requireInvite} apk={null} wearApk={null} />
    {:else if session.user.status !== 'active'}
      <p class="hint">
        Dein Konto wartet noch auf die Freischaltung. Danach kannst du die App koppeln.
      </p>
    {:else if done}
      <p class="ok">
        Fertig. {deviceName ?? 'Das Gerät'} ist jetzt angemeldet — du kannst diesen Tab schließen.
      </p>
    {:else}
      <p>
        Die hotspotter-App auf deinem Gerät möchte sich als <strong>@{session.user.username}</strong>
        anmelden.
      </p>
      {#if code === ''}
        <label>
          <span>Code vom Gerät</span>
          <input
            bind:value={typed}
            autocapitalize="characters"
            autocomplete="off"
            spellcheck="false"
            placeholder="WN32643V"
            maxlength="16"
          />
        </label>
      {:else}
        <p class="code">{entered}</p>
      {/if}
      <p class="hint">
        Gib das nur frei, wenn du die Kopplung gerade selbst in der App gestartet hast. Das Gerät
        bleibt danach 180 Tage angemeldet; du kannst es in den Einstellungen wieder abmelden.
      </p>
      {#if error}<p class="error" role="alert">{error}</p>{/if}
      <button class="primary" disabled={busy || entered === ''} onclick={approve}>
        {busy ? 'Gebe frei …' : 'Ja, das bin ich'}
      </button>
    {/if}
  </section>
</main>

<style>
  main {
    display: grid;
    place-items: center;
    min-height: 100%;
    padding: 1.5rem;
  }

  section {
    display: grid;
    gap: 0.75rem;
    width: min(28rem, 100%);
    padding: 1.5rem;
    border: 1px solid var(--border);
    border-radius: 1rem;
    background: var(--card);
  }

  h1 {
    margin: 0;
    font-size: 1.25rem;
  }

  p {
    margin: 0;
    font-size: 0.9375rem;
  }

  label {
    display: grid;
    gap: 0.3rem;
    font-size: 0.8125rem;
    color: var(--muted);
    text-align: left;
  }

  input {
    width: 100%;
    padding: 0.6rem 0.75rem;
    border: 1px solid var(--border);
    border-radius: 0.6rem;
    background: var(--bg);
    color: var(--fg);
    /* The same monospace-ish treatment the code gets when it arrives in the URL: eight
       characters read off a watch are easier to check against what is on screen. */
    font-size: 1.1rem;
    letter-spacing: 0.12em;
    text-align: center;
    text-transform: uppercase;
  }

  .code {
    padding: 0.75rem;
    border: 1px dashed var(--border);
    border-radius: 0.6rem;
    font-family: ui-monospace, monospace;
    font-size: 1.5rem;
    letter-spacing: 0.25em;
    text-align: center;
  }

  .hint {
    font-size: 0.8125rem;
    color: var(--muted);
  }

  .error {
    font-size: 0.8125rem;
    color: var(--accent);
  }

  .ok {
    font-size: 1rem;
  }

  .primary {
    padding: 0.85rem;
    border: 0;
    border-radius: 0.75rem;
    background: var(--accent);
    color: #fff;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
  }

  .primary:disabled {
    opacity: 0.5;
    cursor: default;
  }
</style>
